/** @prettier */

/**
 * クーポン適用ダイアログ
 */

import {useState, useEffect} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Box,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
//API import
import {
  getBillingCouponApplied,
  postBillingApplyCoupon,
} from '../../API/API_Collection';
//Style import
import '../../css/common/common.css';

import {format} from '../../define';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import {useParams} from 'react-router-dom';

const ApplyCouponDialog = (props: {
  gwID: string;
  isOpenDialog: boolean;
  setIsOpenDialog: (arg0: boolean) => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const {siteID} = useParams();

  const [couponCode, setCouponCode] = useState(''); //クーポンコード
  const [couponApplied, setCouponApplied] = useState(false); //クーポン適用有無フラグ
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenAPIErrorDialog, setIsOpenAPIErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [isOpenCouponErrorDialog, setIsOpenCouponErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  //prettier-ignore
  const [isOpenCouponSuccessDialog, setIsOpenCouponSuccessDialog] = useState(false); //クーポン入力成功時
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [couponCodeError, setCouponCodeError] = useState(false); //クーポンコード

  useEffect(() => {
    if (siteID === undefined) {
      return;
    }
    // クーポン適用状況の取得
    getBillingCouponApplied(siteID, props.gwID)
      .then(response => {
        setCouponApplied(response.coupon === 'applied');
      })
      .catch(() => {
        setIsOpenAPIErrorDialog(true);
      });
  }, []);

  //入力項目のチェック
  useEffect(() => {
    if (couponCode === '' || couponCodeError || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [couponCode, couponCodeError, apiWait]);

  /**
   * 適用ボタン押下時処理
   */
  const applyCoupon = () => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || props.gwID === undefined) {
      return;
    }
    //クーポンコードが取得できない場合
    if (couponCode === undefined) {
      return;
    }

    setAPIWait(true);

    // クーポンを適用
    postBillingApplyCoupon(siteID, props.gwID, {'coupon-code': couponCode})
      .then(response => {
        if (response.result === 'OK') {
          // クーポンの適用に成功した場合
          // クーポン適用成功ダイアログを表示する
          setIsOpenCouponSuccessDialog(true);
        } else {
          // クーポンの適用に失敗した場合
          // クーポン適用失敗ダイアログを表示する
          setIsOpenCouponErrorDialog(true);
        }
      })
      .catch(() => {
        setIsOpenAPIErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };

  /**
   * クーポンエラーダイアログOKボタン押下時処理
   */
  const handleCouponErrorClose = () => {
    setIsOpenCouponErrorDialog(false);
  };

  /**
   * クーポン成功ダイアログOKボタン押下時処理
   */
  const handleCouponSuccessClose = () => {
    setIsOpenCouponSuccessDialog(false);
    props.setIsOpenDialog(false);
  };

  //パスパラメータを取得できない場合
  if (siteID === undefined) return <></>;

  return (
    <>
      <Dialog open={props.isOpenDialog} onClose={handleClose}>
        <DialogTitle>{trans('ApplyCouponDialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {/* クーポン適用済みの場合はメッセージを表示する */}
            {couponApplied && (
              <DialogContentText>
                {/* ダイアログに表示するメッセージ */}
                {trans('ApplyCouponDialog.text.couponApplied')}
              </DialogContentText>
            )}
            {/* クーポンコード */}
            <ChkInputTextbox
              setValue={setCouponCode}
              setError={setCouponCodeError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={trans('ApplyCouponDialog.txtField.label.couponCode')}
              value={couponCode}
              id="mv-inputApplyCouponCode"
              helperText=" "
              errHelperText={trans('ApplyCouponDialog.txtField.errHelperTxt')}
              variant="standard"
              autoFocus={true}
              margin="dense"
              // クーポン適用済みの場合は無効化
              disabled={couponApplied}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* APPLY/CANCELボタンの設定） */}
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('ApplyCouponDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={() => applyCoupon()}
            buttonText={trans('ApplyCouponDialog.buttonTxt.apply')}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      {/* エラー表示 */}
      <APIErrorMessage
        open={isOpenAPIErrorDialog}
        setOpen={setIsOpenAPIErrorDialog}
      />
      {/* 不正クーポン表示 */}
      <Dialog
        open={isOpenCouponErrorDialog}
        onClose={handleCouponErrorClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle
          id="form-dialog-title"
          className="billingTable__errorDialog__title">
          {trans('ApplyCouponDialog.errorDialog.title')}
        </DialogTitle>
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>
            <Box component="span" className="billingTable__errorDialog__text">
              {trans('ApplyCouponDialog.errorDialog.msg')}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PrimaryButton
            onClick={handleCouponErrorClose}
            buttonText={trans('ApplyCouponDialog.buttonTxt.ok')}
          />
        </DialogActions>
      </Dialog>
      {/* クーポン成功表示 */}
      <Dialog
        open={isOpenCouponSuccessDialog}
        onClose={handleCouponSuccessClose}
        aria-labelledby="coupon-success-dialog-title">
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>
            {trans('ApplyCouponDialog.successDialog.msg')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PrimaryButton
            onClick={handleCouponSuccessClose}
            buttonText={trans('ApplyCouponDialog.buttonTxt.ok')}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
export default ApplyCouponDialog;
